import { LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TalkingPointAiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point-ai/talking-point-ai.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { TimelineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.component';
import { ITimelineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITalkingPointAiAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { VideoPlayerComponent } from '@shared/components/video-player/video-player.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { EChartsOption } from 'echarts';
import { MultiMediaPostAnalyticsBaseDirective } from '../multi-media-post-analytics-base/multi-media-post-analytics-base.directive';
import { ITalkingPointAiPostAnalyticsTableData } from './talking-point-ai-post-analytics.interface';

@Component({
  imports: [
    AnalyticsDetailsTableComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
    MatButtonModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    NgTemplateOutlet,
    TalkingPointAiComponent,
    TimelineGraphComponent,
    TranslateModule,
    VideoPlayerComponent,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-talking-point-ai-post-analytics',
  templateUrl: './talking-point-ai-post-analytics.component.html',
})
export class TalkingPointAiPostAnalyticsComponent extends MultiMediaPostAnalyticsBaseDirective<
  ITalkingPointAiPostAnalyticsTableData,
  ITalkingPointAiAnalytics
> {
  public override readonly deliverableType = EDeliverableType.talkingPointAi;
  public override readonly displayedColumns: string[] = ['timestamp', 'message', 'actions'];
  public override readonly dataSource: MatTableDataSource<ITalkingPointAiPostAnalyticsTableData> =
    new MatTableDataSource();

  protected override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.talkingPointAis?.find((tp) => tp.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'talkingPointAi-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPointAi.totalCount'),
    value: 0,
  };

  public override readonly chartConfiguration = signal<Partial<EChartsOption>>({
    title: {
      text: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPointAi.talkingPointAiOverTime'),
    },
  });

  protected override prepareChartAndTableData(): void {
    const tableData: ITalkingPointAiPostAnalyticsTableData[] = [];
    const dotGraphData: ITimelineGraphInputData[] = [];
    const currentTalkingPointAiAnalytics = this.currentDeliverable();
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentTalkingPointAiAnalytics?.findings || []).filter((f) => !removedIds.has(f.id));

    findings.forEach((finding) => {
      const { id } = finding;
      const aggregatedVideoTime = this.calculateAggregatedVideoTime(finding.mediaIndex, finding.videoTime);

      dotGraphData.push({
        id,
        mediaIndex: finding.mediaIndex,
        status: currentTalkingPointAiAnalytics?.status ?? finding.status,
        videoTime: finding.videoTime,
        xAxisValue: aggregatedVideoTime,
      });

      tableData.push({
        aggregatedVideoTime,
        findingParentId: finding.parentId,
        id,
        mediaIndex: finding.mediaIndex ?? 0,
        message: finding.text ?? '',
        reason: finding.reason,
        timestamp: DurationUtilitiesService.convertSecondsToVideoTime(aggregatedVideoTime, true),
        videoTime: finding.videoTime ?? 0,
      });
    });

    tableData.sort((a, b) => (a.aggregatedVideoTime ?? 0) - (b.aggregatedVideoTime ?? 0));

    const topicKey = (currentTalkingPointAiAnalytics?.target ??
      this.deliverableForm().get('text')?.getRawValue() ??
      '') as string;

    this.setKeywordTableRowIds(topicKey && tableData.length > 0 ? { [topicKey]: tableData.map((row) => row.id) } : {});

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
  }

  protected override setCounts(): void {
    const talkingPointAiAnalytics = this.currentDeliverable();

    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const activeCount = (talkingPointAiAnalytics?.findings ?? []).filter((f) => !removedIds.has(f.id)).length;

    this.kpiCardData.value = activeCount;

    AnalyticsDetailsUtilitiesService.mapTextDescriptionDeliverableAnalyticsToForm(
      this.deliverableForm(),
      talkingPointAiAnalytics,
      EDeliverableType.talkingPointAi,
    );
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override getKeywords(): string[] {
    const tp = this.currentDeliverable();
    const topic = (tp?.target ?? this.deliverableForm().get('text')?.getRawValue() ?? '') as string;

    return topic ? [topic] : [];
  }
}
